'use client'

import type { Common, Cruz } from '@cruz-com/lookup'
import _ from 'lodash'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { HiHandThumbUp, HiListBullet, HiOutlineHandThumbUp } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'
import { myCruzApi } from '@/api'
import { Avatar, Button, CruzIcon, Divider, Modal } from '@/components'
import { useGlobalStore } from '@/store/provider'

type Props = {
  name: string
  id: string
  vote: Common.Vote | undefined
  user_id?: string
  my_user_id?: string
  onVoteChanged: () => void
} & React.ComponentPropsWithoutRef<'div'>

const UserListItem = memo(({ user }: { user: Cruz.UserDisplay }) => {
  const name = `${user.first_name} ${user.last_name}`

  return (
    <article className='flex items-center gap-x-4 px-2'>
      <Avatar photo_url={user?.photo_url || ''} className='size-10' name={name} />
      <div className='flex-none text-left'>
        <h3 className='m-0! p-0! text-base! font-medium!'>{name}</h3>
        {!_.isEmpty(user?.location) && (
          <address className='m-0 text-sm font-light not-italic'>
            {user?.location?.city}, {user?.location?.state_abbr}
          </address>
        )}
      </div>
    </article>
  )
})

const LikesModal = memo(
  ({ open, onClose, count, userList }: { open: boolean; onClose: () => void; count: number; userList: Cruz.UserDisplay[] }) => (
    <Modal open={open} setOpen={onClose} className='min-h-24 min-w-[400px] p-0'>
      <section className='flex items-center justify-between rounded-t-lg bg-zinc-100 px-4 py-2 dark:bg-zinc-900'>
        <h2 className='m-0! p-0! text-base! font-light!'>
          <span className='mr-1.5 text-lg font-semibold'>{count}</span>
          {count > 1 ? `people liked this` : ' person liked this'}
        </h2>
        <Button type='button' variant='transparent' onClick={onClose} className='-mr-2 h-12 rounded-full' aria-label='Close likes modal'>
          <CruzIcon.Close className='size-5' aria-hidden='true' />
        </Button>
      </section>
      <Divider />
      <ul className='m-0 flex list-none flex-col justify-center divide-y p-0 pb-2 dark:divide-zinc-700'>
        {_.map(userList, (user, i) => (
          <li key={i} className='py-4'>
            <UserListItem user={user} />
          </li>
        ))}
      </ul>
    </Modal>
  ),
)

const btnStyle = 'px-3 h-full flex items-center hover:bg-zinc-100 dark:hover:bg-zinc-700'

// this is "like" button
export const Like = memo(({ name, id, vote, user_id, my_user_id, className, onVoteChanged }: Props) => {
  const { user, modalSignIn, setModalSignIn } = useGlobalStore((state) => state)
  const [openModal, setOpenModal] = useState(false)
  const [shouldRefresh, setShouldRefresh] = useState(false)
  const [voteUp, setVoteUp] = useState(vote?.up)

  useEffect(() => {
    setVoteUp(vote?.up)
  }, [vote?.up])

  const containerClassName = useMemo(
    () => twMerge('border border-zinc-300 dark:border-zinc-700 rounded h-10', 'flex items-center divide-x dark:divide-zinc-700', className),
    [className],
  )

  const count = useMemo(() => voteUp?.count || 0, [voteUp?.count])

  const userList = useMemo(() => (voteUp?.user_list as Cruz.UserDisplay[]) || [], [voteUp?.user_list])

  const isLiked = useMemo(() => {
    if (!my_user_id) {
      return false
    }
    return !_.isEmpty(_.find(userList, (x) => x?._id === my_user_id))
  }, [my_user_id, userList])

  const isMine = useMemo(() => {
    return !_.isEmpty(my_user_id) && user_id === my_user_id
  }, [my_user_id, user_id])

  const handleCloseModal = useCallback(() => {
    setOpenModal(false)
  }, [])

  const handleOpenModal = useCallback(() => {
    setOpenModal(true)
  }, [])

  // toggle like
  const toggleLike = useCallback(async () => {
    if (_.isEmpty(user)) {
      setShouldRefresh(true)
      setModalSignIn({ show: true, like: { name, id } })
      return
    }
    try {
      const { data } = await myCruzApi.toggleLike({ name, id })
      setVoteUp(data?.up)
      onVoteChanged()
    } catch {}
  }, [user, name, id, setModalSignIn, onVoteChanged])

  useEffect(() => {
    if (shouldRefresh && !modalSignIn?.show) {
      onVoteChanged()
    }
  }, [shouldRefresh, modalSignIn, onVoteChanged])

  if (!vote) {
    return null
  }

  return (
    <>
      <LikesModal open={openModal} onClose={handleCloseModal} count={count} userList={userList} />
      <section className={containerClassName} aria-label='Like actions'>
        <button
          type='button'
          className={btnStyle}
          disabled={isMine}
          title={isLiked ? 'You liked this, click to Unlike' : 'Like'}
          onClick={toggleLike}
        >
          {isLiked ? (
            <HiHandThumbUp className='mr-2 size-5 text-red-800 md:size-4 dark:text-red-700' aria-hidden='true' />
          ) : (
            <HiOutlineHandThumbUp className='mr-2 size-5 md:size-4' aria-hidden='true' />
          )}
          <span className='mr-1 font-normal'>{count.toLocaleString()}</span>
          <span className='text-sm font-normal'>Like{count > 1 && 's'}</span>
        </button>
        {count > 0 && (
          <button type='button' className={btnStyle} onClick={handleOpenModal} title='View who liked this' aria-label='View who liked this'>
            <HiListBullet className='size-5' aria-hidden='true' />
          </button>
        )}
      </section>
    </>
  )
})
